import { z } from "zod";
import { flag, id, nonNegativePct, nullableText } from "./common";

export function isTimeZone(v: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: v });
    return true;
  } catch {
    return false;
  }
}

const intList = z.array(z.number().int().min(0));
const strList = z.array(z.string().trim().min(1));

export const organizationPatch = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  legalName: z.string().trim().max(200).nullable().optional(),
  dba: z.string().trim().max(200).nullable().optional(),
  address: nullableText.optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
  timezone: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine(isTimeZone, "unknown IANA timezone")
    .optional(),
});
export type OrganizationPatch = z.infer<typeof organizationPatch>;

export const companySettingsPatch = z.object({
  // Default deposits are a share of total, unlike explicitly negotiated
  // booking overrides. Never clamp or rewrite an existing agreement here.
  depositPct: nonNegativePct.refine(value => Number(value) <= 100, "Default deposit percentage cannot exceed 100").optional(),
  balanceDaysBefore: z.number().int().min(0).optional(),
  cardFeePct: nonNegativePct.optional(),
  lateFeePct: nonNegativePct.optional(),
  holdAmount: z.number().int().min(0).optional(),
  holdHours: z.number().int().min(1).optional(),
  damageDefaultOn: flag.optional(),
  damageNoticeDays: intList.optional(),
  damageTeamWarnDays: intList.optional(),
  cardHoldDaysBefore: z.number().int().min(0).optional(),
  insuranceDueDaysBefore: z.number().int().min(0).optional(),
  insuranceReminderDays: intList.optional(),
  balanceReminderDays: intList.optional(),
  overdueReminderDays: intList.optional(),
  tourReminderHoursClient: z.number().int().min(0).optional(),
  tourReminderHoursStaff: z.number().int().min(0).optional(),
  autoSendAutomated: flag.optional(),
  /** SPEC §3.11: whether `/q` and `/b` show the venue's preferred vendors. Off
   * by default — recommending a business to a client is the venue's endorsement
   * to make, not ours. */
  showPreferredVendorsToClients: flag.optional(),
  notifyEmails: strList.optional(),
  notifyPhones: strList.optional(),
  smsFromNumber: z.string().trim().max(40).nullable().optional(),
  emailFrom: z.string().trim().max(200).nullable().optional(),
  emailFromName: z.string().trim().max(200).nullable().optional(),
  replyTo: z.string().trim().max(200).nullable().optional(),
  googleCalendarId: z.string().trim().max(200).nullable().optional(),
  otherWaysToPay: nullableText.optional(),
  agentName: z.string().trim().max(120).nullable().optional(),
  agentPhone: z.string().trim().max(40).nullable().optional(),
  defaultCountersignerMemberId: id.nullable().optional(),
  damageRefundDaysAfter: z.number().int().min(0).optional(),
  /** The window automated sends wait in, in the company's own zone (SPEC §7.12).
   * Stored as JSON; a 24-hour clock either side. */
  quietHours: z.object({
    start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a 24-hour time such as 21:00"),
    end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a 24-hour time such as 08:00"),
  }).optional(),
});
export type CompanySettingsPatch = z.infer<typeof companySettingsPatch>;

export const settingsPatch = z.object({ organization: organizationPatch.optional(), settings: companySettingsPatch.optional() });
export type SettingsPatch = z.infer<typeof settingsPatch>;
